import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Datagrid,
  FunctionField,
  ListContextProvider,
  Pagination,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  useList,
  useGetList,
  useStore,
} from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { ScholarshipStatusesChoices, SchorashipTypeChoices } from "utils/choices";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import dataProvider from "server/dataProvider";
import { scholarshipRequests } from "api/scholarshipApi";
import { useNotifyError } from "utils/useNotifyError";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import CustomFilter from "features/CustomFilter";
import { ALL_OPTIONS } from "features/filters/constants";

const STORE_KEY = "scholarshipApplications.filters";

const fullName = rec => [rec.studentFirstName, rec.studentSecondName, rec.studentLastName].filter(Boolean).join(" ") || "-";
const scholarshipTypeName = rec => SchorashipTypeChoices.find(option => option.id === rec.scholarshipType)?.name ?? "-";
const statusName = rec => ScholarshipStatusesChoices.find(option => option.id === rec.status)?.name ?? "-";

const ListScholarshipApplications = () => {
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const [filters, setFilters] = useStore(STORE_KEY, null);

  // Pre-filter draft, committed to `filters` on "Покажи".
  const [campaignId, setCampaignId] = useState(filters?.campaignId ?? null);
  const [scholarshipType, setScholarshipType] = useState(filters?.scholarshipType ?? null);

  const { data: campaigns } = useGetList(scholarshipRequests.getScholarshipCampaignsSelect(), ALL_OPTIONS);

  const changeCampaign = value => {
    setCampaignId(value);
    setScholarshipType(null);
  };

  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const requestIdRef = useRef(0);

  const handleSearch = selectedFilters => {
    setPage(1);
    setFilters(selectedFilters);
  };

  // Reset to page 1 when the page size changes — otherwise a current page beyond the
  // new page count returns an empty result and <Pagination> unmounts (no way back).
  const handlePerPageChange = value => {
    setPerPage(value);
    setPage(1);
  };

  const fetchApplications = useCallback(async () => {
    // Guard against out-of-order responses: only the latest request may apply its result.
    const requestId = ++requestIdRef.current;
    try {
      const { data } = await dataProvider.customRequest(
        scholarshipRequests.getScholarshipApplications({ ...filters, CurrentPage: page, PerPage: perPage }),
        null,
        "GET",
      );
      if (requestId !== requestIdRef.current) return;
      setApplications(data?.approvedApplications?.items ?? []);
      setTotal(data?.approvedApplications?.totalItems ?? 0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      notifyError(err);
    }
  }, [filters, page, perPage, notifyError]);

  useEffect(() => {
    if (!filters) return;
    fetchApplications();
  }, [filters, fetchApplications]);

  // Only `data` is passed to useList — pagination is driven server-side via the
  // overridden context below, so useList's client-side slice never truncates the page.
  const baseListContext = useList({ data: applications, isLoading: false });

  const listContext = {
    ...baseListContext,
    total,
    page,
    perPage,
    setPage,
    setPerPage: handlePerPageChange,
  };

  return (
    <>
      {!filters && <Title title={"Кандидатури за стипендии"} />}
      <CustomFilter
        title="Избери филтри"
        cardWidth={isMobile ? "100%" : "700px"}
        buttonLabel="Покажи"
        filters={[
          {
            label: "Кампания",
            type: "select",
            value: campaignId ?? "",
            setValue: changeCampaign,
            choices: campaigns ?? [],
          },
          ...(campaignId
            ? [
                {
                  label: "Тип стипендия",
                  type: "select",
                  value: scholarshipType ?? "",
                  setValue: setScholarshipType,
                  choices: SchorashipTypeChoices,
                },
              ]
            : []),
        ]}
        buttonDisabled={!campaignId || !scholarshipType}
        buttonOnClick={() => handleSearch({ campaignId, scholarshipType })}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{ display: "flex", justifyContent: "center", gap: 3 }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за кандидатури стипендии.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <ListContextProvider value={listContext}>
          <Title title="Кандидатури за стипендии" />
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={rec => (
                <div>
                  <MobileListRow fields={[{ label: "Име", value: fullName(rec) }]} />
                  <MobileListRow fields={[{ label: "Име кампания", value: rec?.campaignName }]} />
                </div>
              )}
              secondaryText={rec => (
                <div>
                  <MobileListRow fields={[{ label: "Факултетен номер", value: rec?.facultyNumber }]} />
                  <MobileListRow fields={[{ label: "Специалност", value: rec?.specialityName }]} />
                  <MobileListRow fields={[{ label: "Тип стипендия", value: scholarshipTypeName(rec) }]} />
                  <MobileListRow fields={[{ label: "Статус", value: statusName(rec) }]} />
                </div>
              )}
              tertiaryText={() => (
                <SecuredComponent permission={permissions.canPreviewScholarshipApplications}>
                  <ShowButton label="" />
                </SecuredComponent>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={{ mt: 5 }}
            >
              <TextField
                source="campaignName"
                label="Име кампания"
                emptyText="-"
                sortable={false}
              />
              <FunctionField
                label="Име"
                render={fullName}
              />
              <TextField
                source="facultyNumber"
                label="Факултетен номер"
                emptyText="-"
                sortable={false}
              />
              <TextField
                source="specialityName"
                label="Специалност"
                emptyText="-"
                sortable={false}
              />
              <FunctionField
                label="Тип стипендия"
                render={scholarshipTypeName}
              />
              <FunctionField
                label="Статус"
                render={statusName}
              />
              <SecuredComponent permission={permissions.canPreviewScholarshipApplications}>
                <ShowButton />
              </SecuredComponent>
            </Datagrid>
          )}
          <Pagination />
        </ListContextProvider>
      )}
    </>
  );
};

export default ListScholarshipApplications;
